import React, { useState, useEffect } from 'react';
import { 
  Search, 
  FileText, 
  Database, 
  Bot, 
  GitBranch, 
  Settings,
  BookOpen,
  X,
  RefreshCw
} from 'lucide-react';
import { KnowledgeSearchResult } from '../../types';
import apiService from '../../services/api';

interface KnowledgeBasePanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentQuery?: string;
  projectId?: number;
  onSelectKnowledge?: (knowledge: KnowledgeSearchResult) => void;
}

const KnowledgeBasePanel: React.FC<KnowledgeBasePanelProps> = ({
  isOpen,
  onClose,
  currentQuery,
  projectId,
  onSelectKnowledge
}) => {
  const [searchResults, setSearchResults] = useState<KnowledgeSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(currentQuery || '');
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (currentQuery && currentQuery !== searchQuery) {
      setSearchQuery(currentQuery);
      handleSearch(currentQuery);
    }
  }, [currentQuery]);

  const handleSearch = async (query?: string) => {
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const results = await apiService.searchKnowledgeBase({
        query: searchTerm,
        user_id: 1, // This should come from auth context
        project_id: projectId,
        limit: 10,
        similarity_threshold: 0.6
      });
      setSearchResults(results);
      setHasSearched(true);
    } catch (err) {
      setError('Failed to search knowledge base');
      console.error('Error searching knowledge base:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getContentTypeIcon = (contentType: string) => {
    switch (contentType) {
      case 'project':
        return <Database className="w-4 h-4" />;
      case 'orchestration':
        return <GitBranch className="w-4 h-4" />;
      case 'agent':
        return <Bot className="w-4 h-4" />;
      case 'model':
        return <Settings className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getContentTypeLabel = (contentType: string) => {
    switch (contentType) {
      case 'orchestration':
        return 'Workflow';
      case 'project':
        return 'Project';
      case 'agent':
        return 'Agent';
      case 'model':
        return 'Model';
      case 'document':
        return 'Document';
      default:
        return contentType;
    }
  };

  const getContentTypeColor = (contentType: string) => {
    switch (contentType) {
      case 'project':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'orchestration':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'agent':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'model':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const formatContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Knowledge Base</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search knowledge base..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleSearch()}
              disabled={loading || !searchQuery.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span>Search</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {loading && (
            <div className="flex justify-center items-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          )}

          {!loading && hasSearched && searchResults.length === 0 && (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No knowledge base entries found</p>
              <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
            </div>
          )}

          {!loading && searchResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Found {searchResults.length} relevant entries
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Click on an entry to use it in the conversation
                </p>
              </div>
              
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  onClick={() => onSelectKnowledge?.(result)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`p-1 rounded ${getContentTypeColor(result.content_type)}`}>
                        {getContentTypeIcon(result.content_type)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-warm-white">{result.name}</h4>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${getContentTypeColor(result.content_type)}`}>
                          {getContentTypeLabel(result.content_type)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-warm-white">
                        {(result.similarity_score * 100).toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Relevance</div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {formatContent(result.content_text)}
                  </p>
                  
                  {result.content_metadata && Object.keys(result.content_metadata).length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">Metadata:</span> {Object.keys(result.content_metadata).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && !hasSearched && (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Search the knowledge base</p>
              <p className="text-sm text-gray-400 mt-1">
                Enter a query to find relevant information from your projects, workflows, agents, and schemas
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>Knowledge base entries are automatically populated from your projects</span>
            <button
              onClick={() => window.open('/knowledge-base', '_blank')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Manage Knowledge Base
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBasePanel; 